import { onBeforeUnmount, ref, Ref, watch } from "vue";
import { useExplorerStore } from "@/modules/explorer/store";
import { useDocumentVisibility } from "@vueuse/core";
import { useDashboardStore } from "@/modules/dashboards/store";

type fetchDataFn = { (): Promise<any> };

export function addWatcherForRealtime(fetchData: fetchDataFn, fetchTotal?: fetchDataFn) {
	const isTabActive = useDocumentVisibility();
	const baseDelayTime = 5000;
	let refreshDelay = baseDelayTime; // Default delay time in milliseconds

	let backgroundTimerId = null;

	const setTimerWithBackgroundDataFetch = () => {
		backgroundTimerId = setTimeout(async () => {
			// Clear existing timeout just to prevent any issues with multiple timeouts being set.
			destroyTimerForBackgroundDataFetch();

			if (isTabActive.value === "visible") {
				// When the tab is not active we don't need to pointlessly update the graph.
				const startTime = performance.now();
				if (fetchTotal) {
					await fetchTotal();
				}
				await fetchData();
				const endTime = performance.now();

				refreshDelay = endTime - startTime + baseDelayTime;
			}

			// Trigger a new timeout after data is loaded
			// Somehow pass this as a parameter instead of getting it from the store
			// then in the individual watch functions pass the correct ones in.
			if (
				useDashboardStore().dashboardState.isLive ||
				useExplorerStore().document.is_updated_in_background
			) {
				setTimerWithBackgroundDataFetch();
			}
		}, refreshDelay);
	};

	const destroyTimerForBackgroundDataFetch = () => {
		if (backgroundTimerId) {
			clearTimeout(backgroundTimerId);
			backgroundTimerId = null;
		}
	};

	watch(
		() => ({ ...useExplorerStore().document }),
		(newDocument) => {
			if (newDocument.is_updated_in_background) {
				setTimerWithBackgroundDataFetch();
			} else {
				destroyTimerForBackgroundDataFetch();
			}
		},
		{ immediate: true }
	);

	watch(
		() => useDashboardStore().dashboardState.isLive,
		(isLive) => {
			const store = useDashboardStore();
			if (isLive && store.dashboardState.dashboard !== null) {
				store.dashboardState.dashboard.cards.forEach((c) => {
					if (c.stored_document?.activeDocument) {
						c.stored_document.activeDocument.is_updated_in_background = true;
					}
				});
				setTimerWithBackgroundDataFetch();
			} else if (!isLive && store.dashboardState.dashboard !== null) {
				store.dashboardState.dashboard.cards.forEach((c) => {
					if (c.stored_document?.activeDocument) {
						c.stored_document.activeDocument.is_updated_in_background = false;
					}
				});
				destroyTimerForBackgroundDataFetch();
			}
		},
		{ immediate: true }
	);

	onBeforeUnmount(() => destroyTimerForBackgroundDataFetch());
}
